// Certifications (Pat, Oct 5 2026) — Employees → Certifications. Office/Executive: the cards
// waiting for review (picture, dates, approve / send back) and a grid of every employee
// against every card type. PMs: the same grid, status only (who has a current Traffic
// Control Supervisor card, when it runs out) — never the pictures.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch } from '../../services/apiClient';
import { CertRow, CertsPanel, fmtDay } from '../../components/EmployeeCerts';

const CELL = {
  expired: { bg: '#fee2e2', fg: '#b91c1c' }, red: { bg: '#fee2e2', fg: '#b91c1c' }, yellow: { bg: '#fef9c3', fg: '#854d0e' },
  ok: { bg: '#dcfce7', fg: '#166534' }, none: { bg: '#f1f5f9', fg: '#334155' },
};
const mmyy = ymd => { if (!ymd) return '✓'; const d = new Date(ymd + 'T12:00:00'); return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); };

export default function AdminCertifications() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');   // all | attention
  const [person, setPerson] = useState(null);
  const load = useCallback(() => apiFetch('/certs/overview').then(setData).catch(e => setErr(e?.error || 'Could not load the certifications.')), []);
  useEffect(() => { load(); }, [load]);

  const types = (data?.types || []).filter(t => t.key !== 'other');
  const people = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.people || []).filter(p => !s || `${p.name} ${p.role || ''} ${p.trade || ''}`.toLowerCase().includes(s))
      .filter(p => show !== 'attention' || p.certs.some(c => ['expired', 'red', 'yellow'].includes(c.expiry_state)));
  }, [data, q, show]);
  const soon = useMemo(() => (data?.people || []).flatMap(p => p.certs.filter(c => ['expired', 'red', 'yellow'].includes(c.expiry_state)).map(c => ({ ...c, who: p.name, pid: p.id })))
    .sort((a, b) => (a.days_left ?? 0) - (b.days_left ?? 0)), [data]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          {data?.can_manage && <button onClick={() => navigate('/admin/employees')} style={{ background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>← Employees</button>}
          <h1 className="admin-page-title">Certifications</h1>
          <p className="admin-page-sub">{data?.can_manage ? 'Driver’s licenses, OSHA, CPR, NV Energy gas, traffic control, DOT physicals — pictures are seen only by the employee, the office and Executives' : 'Who holds which card and when it runs out'}</p>
        </div>
      </div>
      {err && <div style={{ color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
      {!data && !err && <div style={{ color: '#9ca3af' }}>Loading…</div>}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {data.can_manage && (
            <section>
              <h2 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px' }}>Waiting for review · {data.pending.length}</h2>
              {data.pending.length === 0
                ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Nothing waiting. Cards the crew upload from their phones land here.</div>
                : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 10 }}>
                    {data.pending.map(c => (
                      <div key={c.id}>
                        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a5f', margin: '0 0 4px 2px' }}>{c.user_name}</div>
                        <CertRow c={c} mode="manage" onChanged={load} />
                      </div>
                    ))}
                  </div>}
            </section>
          )}
          {soon.length > 0 && (
            <section>
              <h2 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px' }}>Expired or expiring in 60 days · {soon.length}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {soon.map((c, i) => { const s = CELL[c.expiry_state]; return (
                  <button key={i} onClick={() => data.can_manage && setPerson(data.people.find(p => p.id === c.pid))} style={{ border: 'none', borderRadius: 999, padding: '4px 11px', fontSize: 12.5, fontWeight: 700, background: s.bg, color: s.fg, cursor: data.can_manage ? 'pointer' : 'default' }}>
                    {c.who} · {c.label} · {c.expiry_state === 'expired' ? `expired ${fmtDay(c.expires_on)}` : `${c.days_left} days`}
                  </button>); })}
              </div>
            </section>
          )}
          <section>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, marginRight: 'auto' }}>Everyone</h2>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Name, role or trade" style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: 220 }} />
              <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 9, overflow: 'hidden' }}>
                {[['all', 'All'], ['attention', 'Needs attention']].map(([k, l]) => <button key={k} onClick={() => setShow(k)} style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: show === k ? '#111827' : '#fff', color: show === k ? '#fff' : '#374151' }}>{l}</button>)}
              </div>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0, background: '#f8fafc', minWidth: 170 }}>Employee</th>
                    {types.map(t => <th key={t.key} style={{ padding: '8px 6px', fontWeight: 700, color: '#374151', minWidth: 86 }}>{t.label}</th>)}
                    <th style={{ padding: '8px 6px', fontWeight: 700, color: '#374151' }}>Other</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map(p => {
                    const by = Object.fromEntries(p.certs.map(c => [c.cert_type, c]));
                    const others = p.certs.filter(c => c.cert_type === 'other');
                    return (
                      <tr key={p.id} onClick={() => p.can_manage && setPerson(p)} style={{ borderTop: '1px solid #f1f5f9', cursor: p.can_manage ? 'pointer' : 'default' }}>
                        <td style={{ padding: '7px 10px', position: 'sticky', left: 0, background: '#fff' }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>{[p.role, p.trade].filter(Boolean).join(' · ')}</div>
                        </td>
                        {types.map(t => { const c = by[t.key]; if (!c) return <td key={t.key} style={{ textAlign: 'center', color: '#d1d5db' }}>—</td>;
                          const s = CELL[c.expiry_state] || CELL.none;
                          return <td key={t.key} style={{ textAlign: 'center', padding: 4 }}><span title={c.expires_on ? `Expires ${fmtDay(c.expires_on)}` : 'No expiration'} style={{ display: 'inline-block', borderRadius: 6, padding: '2px 7px', fontWeight: 700, background: s.bg, color: s.fg }}>{c.expiry_state === 'expired' ? '✕ ' : ''}{mmyy(c.expires_on)}</span></td>; })}
                        <td style={{ padding: 4, fontSize: 11.5, color: '#374151' }}>{others.map(c => c.label).join(', ')}</td>
                      </tr>
                    );
                  })}
                  {people.length === 0 && <tr><td colSpan={types.length + 2} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Nobody matches.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>Approved cards only. Green = good · yellow = within 60 days · red = within 30 days or expired{data.can_manage ? ' · click a person to see their cards' : ''}.</div>
          </section>
        </div>
      )}
      {person && (
        <div onClick={() => { setPerson(null); load(); }} style={{ position: 'fixed', inset: 0, zIndex: 5200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 640, maxWidth: '100%', padding: 18, boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 800, flex: 1 }}>🪪 {person.name}</div>
              <button onClick={() => { setPerson(null); load(); }} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <CertsPanel userId={person.id} compact />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
